import React from 'react';
import { Box, Grid, VStack, HStack, Text, Badge } from '@chakra-ui/react';
import { MdEmail, MdPhone, MdPerson, MdShoppingCart } from 'react-icons/md';
import NavigationLink from '../NavigationLink/NavigationLink';
import CopyableText from '@components/CopyableText';
import { formatNumberWithCommas, getOrderStatusColor } from '@utils';
import { ORDER_RESOURCE } from '@constants';

/**
 * Customer Information Panel Component
 * Displays customer and order details with copyable fields
 */
const CustomerInfoPanel = ({ ticket }) => {
  /**
   * Info field with copyable text
   */
  const InfoField = ({ label, value, icon }) => (
    <VStack align="start" spacing={1}>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">
        {label}
      </Text>
      <HStack>
        {icon && <>{icon}</>}
        <CopyableText
          title={label}
          fontSize="sm"
          color="white"
          fontWeight="semibold"
        >
          {value}
        </CopyableText>
      </HStack>
    </VStack>
  );

  return (
    <VStack align="stretch" spacing={4}>
      {/* Customer Details Section */}
      <Box
        p={4}
        bg={'blackAlpha.500'}
        border={'1px solid'}
        borderColor="whiteAlpha.200"
        borderRadius={'xl'}
      >
        {/* Section Title */}
        <HStack mb={4} justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" color="white">
            Customer Information
          </Text>
          {ticket.userId && (
            <NavigationLink
              url={`/user/${ticket.userId}`}
              label="View User Profile"
              icon={<MdPerson />}
              colorScheme="brand"
              size="xs"
              bg="black"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="lg"
              p={2}
            />
          )}
        </HStack>
        {/* Customer Details Grid */}
        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          {/* NOTE :: Hiding this as its not enriched yet*/}
          {/* Customer Name */}
          {/*<VStack align="start" spacing={1}>*/}
          {/*  <Text fontSize="xs" color="gray.500" fontWeight="medium">*/}
          {/*    Customer Name*/}
          {/*  </Text>*/}
          {/*  <Text fontSize="sm" color="white" fontWeight="semibold">*/}
          {/*    {ticket.customerName}*/}
          {/*  </Text>*/}
          {/*</VStack>*/}

          {/* Phone Number */}
          <InfoField
            label="Phone Number"
            value={ticket.userPhoneNumber}
            icon={<MdPhone color="gray" size={16} />}
          />

          {/* Email */}
          <InfoField
            label="Email"
            value={ticket.userEmail}
            icon={<MdEmail color="gray" size={16} />}
          />

          {/* User ID */}
          <InfoField label="User ID" value={ticket.userId} />

          {/* Country */}
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              Country
            </Text>
            <HStack>
              <Text fontSize="sm" color="white" fontWeight="semibold">
                {ticket.userCountry}
              </Text>
              {ticket.countryCode && (
                <Badge colorScheme="blue" fontSize="xs" my={0}>
                  {ticket.countryCode}
                </Badge>
              )}
            </HStack>
          </VStack>
        </Grid>
      </Box>

      {/* Order Details Section */}
      <Box
        p={4}
        bg={'blackAlpha.500'}
        border={'1px solid'}
        borderColor="whiteAlpha.200"
        borderRadius={'xl'}
      >
        <HStack justify="space-between" align="center" mb={4}>
          <Text fontSize="md" fontWeight="semibold" color="white">
            Order Details
          </Text>
          {ticket.referenceId && (
            <NavigationLink
              url={`/user/${ticket.userId}?orderId=${
                ticket.referenceId
              }&orderResource=${
                ticket.orderResource ?? ORDER_RESOURCE.APP_SERVER
              }`}
              label="View Order"
              icon={<MdShoppingCart />}
              colorScheme="brand"
              size="xs"
              bg="black"
              border="1px solid"
              borderColor="whiteAlpha.300"
              borderRadius="lg"
              p={2}
            />
          )}
        </HStack>

        <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
          {/* Order ID */}
          <InfoField label="Order ID" value={ticket.referenceId} />

          {/* Order Amount */}
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              Order Amount
            </Text>
            <Text fontSize="sm" color="white" fontWeight="semibold">
              {formatNumberWithCommas(ticket.orderAmount)}{' '}
              {ticket.sourceCurrency}
            </Text>
          </VStack>

          {/* NOTE :: Hiding this as not enrichedup*/}
          {/* Currency Pair */}
          {/*<VStack align="start" spacing={1}>*/}
          {/*  <Text fontSize="xs" color="gray.500" fontWeight="medium">*/}
          {/*    Currency Pair*/}
          {/*  </Text>*/}
          {/*  <Text fontSize="sm" color="white" fontWeight="semibold">*/}
          {/*    {ticket.sourceCurrency} → {ticket.destinationCurrency}*/}
          {/*  </Text>*/}
          {/*</VStack>*/}

          {/* Order Status */}
          <VStack align="start" spacing={1}>
            <Text fontSize="xs" color="gray.500" fontWeight="medium">
              Order Status
            </Text>
            <Badge
              colorScheme={getOrderStatusColor(ticket.orderStatus)}
              fontSize="xs"
              borderRadius={'xl'}
              px={2}
            >
              {ticket.orderStatus}
            </Badge>
          </VStack>
        </Grid>
      </Box>
    </VStack>
  );
};

export default CustomerInfoPanel;
